import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Search, Download } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { ListViewToggle } from "@/components/ListViewToggle";
import { useListView } from "@/hooks/useListView";
import { Badge } from "@/components/Badge";
import { TipoIcon } from "@/components/TipoIcon";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { PraticheSkeleton } from "@/components/PraticheSkeleton";
import { TIPO_BADGE } from "@/lib/constants";
import { getBollette, downloadBollettaFile } from "@/api/bollette";
import { ApiError } from "@/api/client";
import { formatDateTime } from "@/lib/dates";
import type { BollettaApi } from "@/types";

export function Bollette() {
  const navigate = useNavigate();
  const [items, setItems] = useState<BollettaApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState("Tutte");
  const [toast, setToast] = useState<string | null>(null);
  const [view, setView] = useListView("bollette");

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getBollette()
      .then((res) => setItems(res.items))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          setError("Sessione scaduta. Ricarica la pagina.");
        } else if (e instanceof ApiError && e.status >= 500) {
          setError("Errore del sistema. Riprova tra qualche istante.");
        } else {
          setError("Impossibile caricare le bollette.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filtro === "Tutte" ? items : items.filter((b) => b.tipoFornitura === filtro);

  const columns = [
    {
      key: "data",
      header: "Data",
      render: (b: BollettaApi) => <span className="text-text">{formatDateTime(b.dataBolletta)}</span>,
    },
    {
      key: "fornitore",
      header: "Fornitore attuale",
      render: (b: BollettaApi) => <span className="font-medium text-text">{b.fornitoreAttuale}</span>,
    },
    {
      key: "tipo",
      header: "Tipo",
      render: (b: BollettaApi) => {
        const t = TIPO_BADGE[b.tipoFornitura] ?? { label: b.tipoFornitura, bg: "bg-gray-100", text: "text-gray-800" };
        return <Badge bg={t.bg} text={t.text} className="gap-1"><TipoIcon tipo={b.tipoFornitura} />{t.label}</Badge>;
      },
    },
    {
      key: "consumo",
      header: "Consumo",
      render: (b: BollettaApi) => {
        if (b.kwhAnnui > 0) return <><span className="text-text">{b.kwhAnnui.toLocaleString("it-IT")} kWh/anno</span>{b.consumoStimato && <span className="text-muted-foreground"> (stima)</span>}</>;
        if (b.smcAnnui > 0) return <><span className="text-text">{b.smcAnnui.toLocaleString("it-IT")} Smc/anno</span>{b.consumoStimato && <span className="text-muted-foreground"> (stima)</span>}</>;
        return <span className="text-muted-foreground">—</span>;
      },
    },
    {
      key: "importo",
      header: "Importo/mese",
      render: (b: BollettaApi) => <span className="font-semibold text-text">€{b.importoMensile.toFixed(2)}</span>,
    },
    {
      key: "file",
      header: "File",
      render: (b: BollettaApi) => {
        if (!b.hasFile) return <span className="text-muted-foreground">—</span>;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadBollettaFile(b.id, b.fileName || b.name).catch((err: Error) => {
                showToast(err.message ?? "Errore download");
              });
            }}
            className="text-brand hover:underline text-sm inline-flex items-center gap-1"
          >
            <Download size={14} /> Scarica
          </button>
        );
      },
    },
    {
      key: "dettaglio",
      header: "Dettaglio",
      render: (b: BollettaApi) => (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/bollette/${b.id}`); }}
          className="text-brand hover:underline text-sm"
        >
          Vedi →
        </button>
      ),
    },
  ];

  if (loading) return <PraticheSkeleton />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<FileText size={26} className="text-brand" />}
        title="Nessuna bolletta ancora caricata"
        description="Carica una bolletta dalla consulenza"
      />
    );
  }

  return (
    <div className="space-y-4">
      {toast && (
        <div className="bg-red-100 text-red-800 border border-red-200 px-4 py-3 rounded-lg text-sm fixed bottom-6 right-6 z-50 shadow-lg">
          {toast}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text">Le tue bollette</h2>
          <p className="text-sm text-muted-foreground">Archivio delle bollette caricate</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <ListViewToggle view={view} onChange={setView} />
          {["Tutte", "Luce", "Gas", "Dual"].map((f) => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filtro === f
                  ? "bg-brand text-white"
                  : "bg-card border border-border text-text hover:bg-surface"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon={<Search size={26} className="text-brand" />} title={`Nessuna bolletta di tipo ${filtro}`} />
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          view={view}
          onRowClick={(b) => navigate(`/bollette/${b.id}`)}
          renderCard={(b) => {
            const t = TIPO_BADGE[b.tipoFornitura] ?? { label: b.tipoFornitura, bg: "bg-gray-100", text: "text-gray-800" };
            return (
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-text truncate">{b.fornitoreAttuale}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{formatDateTime(b.dataBolletta)}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge bg={t.bg} text={t.text} className="gap-1"><TipoIcon tipo={b.tipoFornitura} />{t.label}</Badge>
                    <span className="text-xs font-medium text-text">€{b.importoMensile.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
}
